import { useEffect, useState } from "react";
import { ContractTypeSelect } from "@/components/form/ContractTypeSelect";
import { SupportingDocumentsList } from "@/components/review/SupportingDocumentsList";
import { SupportingDocumentsUploader } from "@/components/wizard/SupportingDocumentsUploader";
import { Step2Config } from "@/components/wizard/Step2Config";
import { fetchSupportingDocuments } from "@/lib/api/supporting-documents";
import type { ReviewFormData, SupportingDocInput } from "@/types";
import { MAX_SUPPORTING_DOCS } from "@/types";

export interface ReviewSettingsFieldsProps {
  reviewId: string;
  formData: ReviewFormData;
  onFormDataChange: React.Dispatch<React.SetStateAction<ReviewFormData>>;
  newSupportingDocs: SupportingDocInput[];
  onNewSupportingDocsChange: (docs: SupportingDocInput[]) => void;
  onEmailClick?: () => void;
  /** When false, skip loading existing doc count (e.g. sheet closed). */
  active?: boolean;
}

export function ReviewSettingsFields({
  reviewId,
  formData,
  onFormDataChange,
  newSupportingDocs,
  onNewSupportingDocsChange,
  onEmailClick,
  active = true,
}: ReviewSettingsFieldsProps) {
  const [existingDocCount, setExistingDocCount] = useState(0);
  const [, setSupportingDocsVersion] = useState(0);

  useEffect(() => {
    if (!active) return;
    void fetchSupportingDocuments(reviewId)
      .then((rows) => setExistingDocCount(rows.length))
      .catch(() => setExistingDocCount(0));
  }, [active, reviewId]);

  return (
    <>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-slate-200">
          Background information
          <span className="ml-1 text-xs font-normal text-slate-500">(Optional)</span>
        </label>
        <textarea
          value={formData.backgroundInfo}
          onChange={(e) => onFormDataChange((f) => ({ ...f, backgroundInfo: e.target.value }))}
          placeholder="Context about the contract, counterparty, or concerns…"
          rows={4}
          className="w-full resize-none rounded-lg border border-slate-700 bg-slate-900/50 px-4 py-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      <SupportingDocumentsList
        reviewId={reviewId}
        canDelete
        onChanged={() => {
          setSupportingDocsVersion((v) => v + 1);
          void fetchSupportingDocuments(reviewId)
            .then((rows) => setExistingDocCount(rows.length))
            .catch(() => setExistingDocCount(0));
        }}
      />

      {existingDocCount < MAX_SUPPORTING_DOCS ? (
        <SupportingDocumentsUploader
          docs={newSupportingDocs}
          onChange={onNewSupportingDocsChange}
          maxDocs={Math.max(0, MAX_SUPPORTING_DOCS - existingDocCount)}
          onEmailClick={onEmailClick}
        />
      ) : null}

      <ContractTypeSelect
        value={formData.contractType}
        onChange={(value) => onFormDataChange((f) => ({ ...f, contractType: value }))}
      />

      <Step2Config
        jurisdiction={formData.jurisdiction}
        onJurisdictionChange={(value) => onFormDataChange((f) => ({ ...f, jurisdiction: value }))}
        userRole={formData.userRole}
        onUserRoleChange={(value) => onFormDataChange((f) => ({ ...f, userRole: value }))}
        agentInstructions={formData.agentInstructions}
        onAgentInstructionsChange={(value) => onFormDataChange((f) => ({ ...f, agentInstructions: value }))}
        skipResearch={formData.skipResearch}
        onSkipResearchChange={(value) => onFormDataChange((f) => ({ ...f, skipResearch: value }))}
      />
    </>
  );
}
